import { Button, EmptyState, Icon, Select, Skeleton, useToast } from '@/components';
import type { ReleaseStatus } from '@uchat-team-management/shared';
import { Show, createSignal } from 'solid-js';
import { ChangelogView } from './ChangelogView';
import { useRefreshReleaseTasks, useReleaseChangelog } from './hooks';

export const ChangeLogTabs = (props: {
  releaseId: string;
  status: ReleaseStatus;
}) => {
  const [against, setAgainst] = createSignal<'baseline' | 'published'>('baseline');

  // No explicit baseReleaseId: the server resolves the persisted baseline.
  const changelog = useReleaseChangelog(() => ({
    releaseId: props.releaseId,
    against: against(),
  }));

  const data = () => changelog.data?.data;
  const isDraft = () => props.status === 'draft';

  const toast = useToast();
  const refreshTasks = useRefreshReleaseTasks(props.releaseId);
  const hasExternalTasks = () => (data()?.tasks ?? []).length > 0;

  return (
    <div class="mt-6 rounded-xl border border-neutral-200 bg-surface p-4 dark:border-neutral-800 dark:bg-surface-dark">
      <div class="mb-2 flex flex-wrap items-center justify-between gap-2">
        <h2 class="font-medium">
          Change log{' '}
          <Show when={data()}>
            {(d) => (
              <span class="text-xs font-normal text-neutral-500">
                vs {d().baseLabel} {d().frozenAt ? '(frozen)' : '(preview)'}
              </span>
            )}
          </Show>
        </h2>
        <div class="flex items-center gap-2">
          <Show when={hasExternalTasks()}>
            <Button
              variant="secondary"
              loading={refreshTasks.isPending}
              onClick={() =>
                refreshTasks.mutate(
                  { against: against() },
                  {
                    onError: (e) => toast.error(e instanceof Error ? e.message : 'Refresh failed'),
                  },
                )
              }
            >
              <Show when={!refreshTasks.isPending}>
                <Icon name="rotate" />
              </Show>
              Refresh tasks
            </Button>
          </Show>
          <Show when={isDraft()}>
            <Button
              variant="secondary"
              loading={changelog.isFetching}
              onClick={() => changelog.refetch()}
            >
              <Show when={!changelog.isFetching}>
                <Icon name="rotate" />
              </Show>
              Refresh log
            </Button>
          </Show>
          <Show
            when={
              props.status === 'succeeded' ||
              props.status === 'publishing' ||
              props.status === 'published'
            }
          >
            <Select
              value={against()}
              onChange={(v) => setAgainst(v as 'baseline' | 'published')}
              options={[
                { value: 'baseline', label: 'Confirm snapshot' },
                { value: 'published', label: 'vs previous published' },
              ]}
              class="min-w-44"
            />
          </Show>
        </div>
      </div>

      <Show when={!changelog.isPending} fallback={<Skeleton class="h-24" />}>
        <Show
          when={data()}
          fallback={<EmptyState variant="error" title="Failed to load change log" />}
        >
          {(d) => (
            <div
              aria-busy={changelog.isFetching}
              class="transition-opacity duration-200 ease-out motion-reduce:transition-none"
              classList={{ 'opacity-50': changelog.isFetching }}
            >
              <ChangelogView changelog={d()} />
            </div>
          )}
        </Show>
      </Show>
    </div>
  );
};
